import { getWebAnalyticsDeviceContext } from './deviceContext.js'
import {
  getMallEventUserContext,
  mallAnalyticsLogEvent,
} from './mallEventLog.js'

export const LOW_CODE_INTERACTION_EVENT = 'low_code_interaction'

const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/
const EVENT_PARAM_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,39}$/
const RESERVED_NAME_PREFIXES = ['firebase_', 'google_', 'ga_']
const MAX_PARAM_STRING_LENGTH = 500

function getWindow() {
  return typeof window !== 'undefined' ? window : null
}

export function isValidLowCodeTrackingName(value) {
  const name = String(value || '').trim()
  return (
    EVENT_NAME_PATTERN.test(name)
    && !RESERVED_NAME_PREFIXES.some(prefix => name.startsWith(prefix))
  )
}

export function isValidLowCodeTrackingParamName(value) {
  return EVENT_PARAM_NAME_PATTERN.test(String(value || '').trim())
}

export function hasEnabledLowCodeTracking(config, trigger = 'click') {
  return Boolean(
    config?.enabled === true
    && config?.trigger === trigger
    && isValidLowCodeTrackingName(config?.trackingCode),
  )
}

function getValueByPath(source, path) {
  if (!source || !path)
    return undefined
  const keys = String(path)
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map(item => item.trim())
    .filter(Boolean)
  return keys.reduce((value, key) => value?.[key], source)
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean')
    return value
  if (typeof value === 'number')
    return value !== 0
  const normalized = String(value ?? '').trim().toLowerCase()
  if (['true', '1', 'yes', 'on'].includes(normalized))
    return true
  if (['false', '0', 'no', 'off'].includes(normalized))
    return false
  return undefined
}

function normalizeParamValue(value, valueType = 'string') {
  if (value === undefined || value === null || value === '')
    return undefined
  if (valueType === 'number') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  if (valueType === 'boolean')
    return normalizeBoolean(value)
  if (typeof value === 'object')
    return undefined
  return String(value).slice(0, MAX_PARAM_STRING_LENGTH)
}

function resolveJavascriptValue(value, context, forceExecution = false) {
  if (typeof value !== 'string')
    return value

  const hasJavascriptProtocol = /^javascript:/i.test(value.trim())
  if (!forceExecution && !hasJavascriptProtocol)
    return value

  const win = getWindow()
  if (!win)
    return undefined

  const expression = value
    .trim()
    .replace(/^javascript:/i, '')
    // Legacy low-code JSON may escape property separators and underscores.
    .replace(/\\([._])/g, '$1')
    .replace(/;+\s*$/, '')
    .trim()
  if (!expression)
    return undefined

  const args = [
    'window',
    'document',
    'element',
    'page',
    'binding',
    'runtime',
  ]
  let executor
  try {
    // Prefer expression mode so concise values such as page.campaign.id work.
    // eslint-disable-next-line no-new-func
    executor = new Function(...args, `"use strict"; return (${expression});`)
  }
  catch {
    try {
      // Fall back to function-body mode for multi-line logic with an explicit return.
      // eslint-disable-next-line no-new-func
      executor = new Function(...args, `"use strict"; ${expression}`)
    }
    catch {
      return undefined
    }
  }

  try {
    return executor(
      win,
      win.document,
      context.element,
      context.page,
      context.binding,
      context.runtime,
    )
  }
  catch {
    // A broken dynamic parameter must not interrupt the click action or event upload.
    return undefined
  }
}

function resolveParamSource(param, context) {
  let value
  if (param?.source === 'javascript') {
    value = [param.script, param.value, param.path].find(item => (
      typeof item === 'string' && item.trim()
    ))
    return resolveJavascriptValue(value, context, true)
  }
  else if (param?.source === 'static') {
    value = param.value
  }
  else {
    const sourceMap = {
      element: context.element,
      page: context.page,
      binding: context.binding,
      runtime: context.runtime,
    }
    value = getValueByPath(sourceMap[param?.source], param?.path)
  }
  return resolveJavascriptValue(value, context)
}

function firstTextValue(source, keys) {
  for (const key of keys) {
    const value = getValueByPath(source, key)
    if (value !== undefined && value !== null && value !== '')
      return String(value).slice(0, MAX_PARAM_STRING_LENGTH)
  }
  return ''
}

function buildCommonParams(config, context) {
  const win = getWindow()
  const deviceContext = getWebAnalyticsDeviceContext(win)
  const runtime = context.runtime || {}
  const pagePath = firstTextValue(context.page, ['fullPath', 'path', 'routePath'])
    || `${win?.location?.pathname || ''}${win?.location?.search || ''}${win?.location?.hash || ''}`
  const pageUrl = firstTextValue(context.page, ['pageUrl', 'page_url', 'href'])
    || win?.location?.href
    || ''
  const pageCode = firstTextValue(context.page, ['pageCode', 'page_code', 'code', 'id'])
    || win?.location?.pathname
    || 'unknown_page'
  const locale = context.locale
    || win?.document?.documentElement?.lang
    || win?.navigator?.language
    || 'unknown'
  const elementIndex = Array.isArray(context.elementIndex)
    ? context.elementIndex.join('.')
    : String(context.elementIndex ?? '')
  const memberCode = firstTextValue(runtime, ['memberCode', 'member_code'])
  const explicitLoggedIn = normalizeBoolean(runtime.loggedIn ?? runtime.is_logged_in)
  const isLoggedIn = explicitLoggedIn === undefined
    ? Boolean(memberCode || firstTextValue(runtime, ['userId', 'user_id']))
    : explicitLoggedIn

  return {
    tracking_code: String(config.trackingCode).trim(),
    tracking_trigger: config.trigger || 'click',
    config_version: Number(config.version) || 1,
    page_code: String(pageCode).slice(0, MAX_PARAM_STRING_LENGTH),
    page_path: String(pagePath).slice(0, MAX_PARAM_STRING_LENGTH),
    page_url: String(pageUrl).slice(0, MAX_PARAM_STRING_LENGTH),
    element_id: firstTextValue(context.element, ['id']),
    element_type: String(context.elementType || '').slice(0, MAX_PARAM_STRING_LENGTH),
    element_index: elementIndex.slice(0, MAX_PARAM_STRING_LENGTH),
    locale: String(locale).slice(0, MAX_PARAM_STRING_LENGTH),
    ...deviceContext,
    is_logged_in: isLoggedIn,
    member_code: memberCode,
    user_agent: String(win?.navigator?.userAgent || '').slice(0, MAX_PARAM_STRING_LENGTH),
  }
}

function buildComponentEventParams(context) {
  const eventParams = context?.eventParams
  if (!eventParams || typeof eventParams !== 'object' || Array.isArray(eventParams))
    return {}

  return Object.entries(eventParams).reduce((result, [rawName, rawValue]) => {
    const name = String(rawName || '').trim()
    if (!EVENT_PARAM_NAME_PATTERN.test(name))
      return result
    const valueType = typeof rawValue
    if (!['string', 'number', 'boolean'].includes(valueType))
      return result
    const value = normalizeParamValue(rawValue, valueType)
    if (value !== undefined)
      result[name] = value
    return result
  }, {})
}

export function buildLowCodeTrackingParams(config, context = {}) {
  if (!hasEnabledLowCodeTracking(config, config?.trigger || 'click'))
    return null

  const resolvedContext = {
    ...context,
    runtime: {
      ...getMallEventUserContext(context.app),
      ...(context.runtime || {}),
    },
  }

  const customParams = {}
  const params = Array.isArray(config?.params) ? config.params : []
  params.forEach((param) => {
    const name = String(param?.name || '').trim()
    if (!isValidLowCodeTrackingParamName(name))
      return
    const value = normalizeParamValue(
      resolveParamSource(param, resolvedContext),
      param?.valueType,
    )
    if (value !== undefined)
      customParams[name] = value
  })

  return {
    ...customParams,
    ...buildComponentEventParams(resolvedContext),
    ...buildCommonParams(config, resolvedContext),
  }
}

export function trackLowCodeEvent(config, context = {}) {
  if (!hasEnabledLowCodeTracking(config, context.trigger || 'click'))
    return false
  const gaEventName = String(config?.gaEventName || config?.trackingCode || '').trim()
  if (!isValidLowCodeTrackingName(gaEventName))
    return false
  const params = buildLowCodeTrackingParams(config, context)
  if (!params)
    return false

  try {
    return mallAnalyticsLogEvent(
      LOW_CODE_INTERACTION_EVENT,
      {
        ...params,
        ga_event_name: gaEventName,
      },
      context.app,
      { action: gaEventName },
    )
  }
  catch {
    // Tracking is a best-effort side channel and must never interrupt UI actions.
    return false
  }
}
